/**
 * Финансовый профиль: в каком положении человек сейчас.
 *
 * Не сегмент и не тип личности, а derived state: пересчитывается вместе с
 * остальным и нигде не хранится. Продукт работает не с «нашим пользователем»,
 * а с состояниями, которые могут существовать одновременно — плавающий доход,
 * долги, слабый контроль расходов и невозможность копить.
 *
 * Отдельное измерение — **качество данных**. Пропущенная анкета это недостаток
 * сведений, а не доказанная неспособность копить, и смешивать одно с другим
 * значило бы выносить человеку приговор за то, что он не ответил на вопрос.
 */
import { ZERO, add, clampPos, neg, share, sub, type Money } from './money.js';
import { parts, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { conservativeIncome } from './available.js';
import { monthlyObligations } from './instances.js';
import { computeLiquidity, type LiquiditySafety } from './runway.js';
import { EXPENSIVE_RATE_PCT, hasExpensiveDebt, totalDebt } from './loans.js';
import { creditState } from './credit.js';
import type { AnalyticsTx } from './analytics.js';
import type {
  Account, CreditTerms, Goal, IncomeStream, Loan, Obligation, Reserve,
} from './types.js';

export type PredictabilityLevel = 'high' | 'medium' | 'low';
export type CashflowHealth = 'surplus' | 'tight' | 'deficit' | 'unknown';
export type DebtPressure = 'none' | 'manageable' | 'high';
export type SpendingVisibility = 'high' | 'medium' | 'low' | 'unknown';
export type SavingAbility = 'stable' | 'sometimes' | 'none' | 'unknown';

/** Событие цели: взнос отличается от пропуска. Форма совпадает с журналом. */
export interface GoalEventLike {
  goalId: string;
  cycleStart: PlainDate;
  kind: 'contribution' | 'skip' | 'withdrawal' | string;
  amount: Money;
}

export interface DataQuality {
  /** Есть хотя бы один источник дохода с суммой. */
  incomeKnown: boolean;
  /** Обычные траты названы человеком, а не подставлены нулём. */
  expensesKnown: boolean;
  /** Есть обязательства или человек прямо подтвердил их отсутствие. */
  obligationsKnown: boolean;
  /** История трат достаточна, чтобы говорить о прозрачности расходов. */
  spendingHistoryKnown: boolean;
  /** Есть журнал взносов — иначе про способность копить сказать нечего. */
  savingHistoryKnown: boolean;
  /** На какую дату назван самый старый из остатков. */
  balancesDated?: PlainDate;
}

export interface FinancialProfile {
  incomePredictability: PredictabilityLevel;
  cashflowHealth: CashflowHealth;
  debtPressure: DebtPressure;
  spendingVisibility: SpendingVisibility;
  savingAbility: SavingAbility;
  liquiditySafety: LiquiditySafety | 'unknown';
  dataQuality: DataQuality;
  /** Почему каждое измерение получило своё значение — для отладки и для ИИ. */
  rationale: Record<string, string>;
}

export interface FinancialProfileInput {
  today: PlainDate;
  accounts: readonly Account[];
  reserves?: readonly Reserve[];
  incomeStreams: readonly IncomeStream[];
  obligations: readonly Obligation[];
  obligationsKnown?: boolean;
  loans?: readonly Loan[];
  creditTerms?: readonly CreditTerms[];
  goals: readonly Goal[];
  goalEvents?: readonly GoalEventLike[];
  transactions?: readonly AnalyticsTx[];
  needBaseline: Amount;
  wantBaseline: Amount;
}

/** Доля свободных денег, ниже которой план ломается от первой неожиданности. */
const SURPLUS_SHARE = 0.15;
/** Принятый в кредитовании порог долговой нагрузки. */
const DEBT_SERVICE_SHARE = 0.35;
/** Меньше этого числа трат за месяц — говорить о прозрачности рано. */
const MIN_TX_FOR_VISIBILITY = 20;
/** Сколько последних циклов смотрим на способность копить. */
const SAVING_CYCLES = 3;

export function computeFinancialProfile(input: FinancialProfileInput): FinancialProfile {
  const rationale: Record<string, string> = {};
  const active = input.incomeStreams.filter((s) => s.isActive);
  const loans = input.loans ?? [];

  // 1. Предсказуемость дохода — по составу источников.
  const incomePredictability: PredictabilityLevel = active.length === 0
    ? 'low'
    : active.every((s) => !s.isVariable) ? 'high' : 'medium';
  rationale.incomePredictability = active.length === 0
    ? 'Источников дохода нет'
    : active.every((s) => !s.isVariable)
      ? 'Все источники с фиксированной суммой'
      : 'Есть источник с плавающей суммой';

  // 2. Денежный поток месяца.
  const income = add(...active.map(conservativeIncome));
  const obligationsMonthly = monthlyObligations(input.obligations, input.today);
  let cashflowHealth: CashflowHealth = 'unknown';
  if (!isKnown(input.needBaseline) || active.length === 0) {
    rationale.cashflowHealth = !isKnown(input.needBaseline)
      ? 'Обычные траты неизвестны'
      : 'Доход не указан';
  } else {
    const freeCash = sub(income, add(obligationsMonthly, input.needBaseline.value));
    cashflowHealth = freeCash <= ZERO
      ? 'deficit'
      : freeCash >= share(income, SURPLUS_SHARE) ? 'surplus' : 'tight';
    rationale.cashflowHealth = `Доход ${income}, обязательные ${obligationsMonthly}, жизнь ${input.needBaseline.value}`;
  }

  // 3. Долговая нагрузка.
  const debt = totalDebt(loans);
  const minPayments = add(
    ...(input.creditTerms ?? []).map((t) => {
      const acc = input.accounts.find((a) => a.id === t.accountId);
      return acc ? (creditState(acc, t, input.today).minPayment ?? ZERO) : ZERO;
    }),
  );
  const debtService = add(
    ...loans.filter((l) => l.kind !== 'credit_card').map((l) => l.payment),
    minPayments,
  );
  let debtPressure: DebtPressure;
  if (debt <= ZERO) {
    debtPressure = 'none';
    rationale.debtPressure = 'Долгов нет';
  } else if (income > ZERO && debtService >= share(income, DEBT_SERVICE_SHARE)) {
    debtPressure = 'high';
    rationale.debtPressure = `Обслуживание долга ${debtService} против дохода ${income}`;
  } else if (hasExpensiveDebt(loans)) {
    debtPressure = 'high';
    rationale.debtPressure = `Есть долг дороже ${EXPENSIVE_RATE_PCT}% годовых`;
  } else {
    debtPressure = 'manageable';
    rationale.debtPressure = 'Долги есть, нагрузка умеренная';
  }

  // 4. Прозрачность расходов — по фактическим операциям месяца.
  const month = parts(input.today);
  const monthKey = `${month.year}-${String(month.month).padStart(2, '0')}`;
  const txs = (input.transactions ?? []).filter((t) => t.occurredDate.startsWith(monthKey));
  const spends = txs.filter((t) => t.type === 'expense');
  const adjustments = txs.filter((t) => t.type === 'adjustment');
  const spendSum = add(...spends.map((t) => t.amount));
  const categorized = add(...spends.filter((t) => t.categoryId).map((t) => t.amount));
  // Корректировка бывает и в плюс, и в минус: шум — её размер, а не знак.
  const adjustmentSum = add(...adjustments.map((t) => (t.amount < ZERO ? neg(t.amount) : t.amount)));
  let spendingVisibility: SpendingVisibility;
  if (spends.length < MIN_TX_FOR_VISIBILITY) {
    spendingVisibility = 'unknown';
    rationale.spendingVisibility = `Операций за месяц ${spends.length} — судить рано`;
  } else {
    const known = spendSum > ZERO ? categorized / spendSum : 0;
    const noise = add(spendSum, adjustmentSum) > ZERO
      ? adjustmentSum / add(spendSum, adjustmentSum)
      : 0;
    spendingVisibility = known >= 0.8 && noise <= 0.15 ? 'high' : known >= 0.5 ? 'medium' : 'low';
    rationale.spendingVisibility = `С категорией ${Math.round(known * 100)}%, корректировок ${Math.round(noise * 100)}%`;
  }

  // 5. Способность копить — по журналу взносов, где взнос отличим от пропуска.
  const events = input.goalEvents ?? [];
  const activeGoals = input.goals.filter((g) => g.status === 'active');
  let savingAbility: SavingAbility;
  if (activeGoals.length === 0) {
    savingAbility = 'unknown';
    rationale.savingAbility = 'Активных целей нет';
  } else if (events.length === 0) {
    savingAbility = 'unknown';
    rationale.savingAbility = 'Истории взносов ещё нет';
  } else {
    const cycles = [...new Set(events.map((e) => e.cycleStart))].sort().slice(-SAVING_CYCLES);
    const funded = cycles.filter((c) => events.some(
      (e) => e.cycleStart === c && e.kind === 'contribution' && e.amount > ZERO,
    ));
    savingAbility = funded.length === cycles.length && cycles.length >= SAVING_CYCLES
      ? 'stable'
      : funded.length > 0 ? 'sometimes' : 'none';
    rationale.savingAbility = `Взносы в ${funded.length} из ${cycles.length} последних циклов`;
  }

  // 6. Ликвидность — состояние здесь и сейчас.
  const liquidity = computeLiquidity({
    today: input.today,
    accounts: input.accounts,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    obligations: input.obligations,
    needBaseline: input.needBaseline,
    wantBaseline: input.wantBaseline,
  });
  const liquiditySafety = liquidity.known ? liquidity.safety : 'unknown';
  rationale.liquiditySafety = liquidity.known
    ? `Свободно ${liquidity.free}, дней жизни ${liquidity.days ?? '—'}`
    : 'Обычные траты неизвестны';

  const dated = input.accounts
    .map((a) => a.balanceAsOf)
    .filter((x): x is PlainDate => Boolean(x))
    .sort();

  return {
    incomePredictability,
    cashflowHealth,
    debtPressure,
    spendingVisibility,
    savingAbility,
    liquiditySafety,
    dataQuality: {
      incomeKnown: active.length > 0 && income > ZERO,
      expensesKnown: isKnown(input.needBaseline),
      obligationsKnown: input.obligationsKnown === true || input.obligations.some((o) => o.isActive),
      spendingHistoryKnown: spends.length >= MIN_TX_FOR_VISIBILITY,
      savingHistoryKnown: events.length > 0,
      ...(dated[0] ? { balancesDated: dated[0] } : {}),
    },
    rationale,
  };
}

/**
 * Чего не хватает для полной картины — в порядке пользы.
 *
 * Это и есть замена проценту точности: человеку показывают, что уточнить,
 * а не насколько он «неполон».
 */
export function missingFacts(p: FinancialProfile): string[] {
  const out: string[] = [];
  if (!p.dataQuality.incomeKnown) out.push('доход');
  if (!p.dataQuality.expensesKnown) out.push('обычные траты');
  if (!p.dataQuality.obligationsKnown) out.push('обязательные платежи');
  return out;
}

/** Свободные деньги месяца — то же число, что в профиле. */
export const monthlyFreeCash = (income: Money, obligations: Money, need: Money): Money =>
  clampPos(sub(income, add(obligations, need)));
